import React, { useState, useMemo } from 'react';
import { doc, updateDoc, writeBatch, collection } from 'firebase/firestore';
import { db } from '../firebase';
import { cn, calculateOVR } from '../lib/utils';
import { showSaveError } from './Toast';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronLeft, X } from 'lucide-react';

interface Props {
  teamId: string;
  players: any[];
  isElite: boolean;
  onComplete: () => void;
  onClose: () => void;
  onRequestEliteTrial: (rankedPlayers: any[]) => void;
}

const DEFAULT_ATTRIBUTES = {
  hitting: { contact: 3, power: 3, eye: 3, approach: 3 },
  fielding: { glove: 3, arm: 3, block: 3 },
  intangibles: { speed: 3, iq: 3, clutch: 3 },
  pitching: { velocity: 0, control: 0 },
};

function mergeAttributes(existing: any) {
  if (!existing) return DEFAULT_ATTRIBUTES;
  return {
    hitting: { ...DEFAULT_ATTRIBUTES.hitting, ...existing.hitting },
    fielding: { ...DEFAULT_ATTRIBUTES.fielding, ...existing.fielding },
    intangibles: { ...DEFAULT_ATTRIBUTES.intangibles, ...existing.intangibles },
    pitching: { ...DEFAULT_ATTRIBUTES.pitching, ...existing.pitching },
  };
}

export const RosterScoutingSession: React.FC<Props> = ({
  teamId, players, isElite, onComplete, onClose, onRequestEliteTrial,
}) => {
  const unscouted = players.filter(p => !p.isGuest);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [attrs, setAttrs] = useState<any>(() => mergeAttributes(unscouted[0]?.attributes));
  const [isPitcher, setIsPitcher] = useState(() => {
    const p = unscouted[0];
    return (p?.attributes?.pitching?.velocity ?? 0) > 0 || (p?.attributes?.pitching?.control ?? 0) > 0;
  });
  const [saving, setSaving] = useState(false);
  const [phase, setPhase] = useState<'scouting' | 'reveal'>('scouting');
  const [savedPlayers, setSavedPlayers] = useState<any[]>([]);

  const player = unscouted[currentIndex];

  const liveOVR = useMemo(() => {
    const a = isPitcher ? attrs : { ...attrs, pitching: { velocity: 0, control: 0 } };
    const ovr = calculateOVR(a, player?.primaryPosition ?? '');
    return Math.round(ovr);
  }, [attrs, isPitcher, player]);

  const updateAttr = (category: string, key: string, value: number) => {
    setAttrs((prev: any) => ({
      ...prev,
      [category]: { ...prev[category], [key]: value },
    }));
  };

  const saveAndAdvance = async () => {
    if (saving || !player) return;
    setSaving(true);

    const finalAttrs = isPitcher ? attrs : { ...attrs, pitching: { velocity: 0, control: 0 } };
    const ovr = calculateOVR(finalAttrs, player.primaryPosition ?? '');

    try {
      await updateDoc(doc(db, `teams/${teamId}/players`, player.id), {
        attributes: finalAttrs,
        ovr: Math.round(ovr),
        ovrBreakdown: { scoutingOVR: Math.round(ovr), statDelta: 0, blendedOVR: Math.round(ovr) },
      });
    } catch (error) {
      // Without this, a failed write silently drops the coach's scouting AND
      // leaves the Save button stuck on `saving`.
      showSaveError(`${player.name}'s scouting`, error);
      setSaving(false);
      return;
    }

    const updated = { ...player, attributes: finalAttrs, ovr: Math.round(ovr) };
    const newSaved = [...savedPlayers, updated];
    setSavedPlayers(newSaved);

    const nextIndex = currentIndex + 1;
    if (nextIndex >= unscouted.length) {
      // All players scouted — mark checklist and go to reveal. The checklist
      // flag is cosmetic (Home-tab checkmark), so a failure shouldn't block
      // the reveal — but it should still be visible somewhere.
      try {
        await updateDoc(doc(db, 'teams', teamId), {
          'settings.onboardingChecklist.rosterScouted': true,
        });
      } catch (error) {
        console.error('Failed to mark rosterScouted checklist item:', error);
      }
      setSaving(false);

      // Merge any players not yet scouted this session
      const allRanked = [
        ...newSaved,
        ...players.filter(p => !newSaved.find((s: any) => s.id === p.id)),
      ].sort((a, b) => (b.ovr ?? 0) - (a.ovr ?? 0));

      // EVERY coach sees the reveal, Elite or not. A coach has just rated their
      // whole roster one player at a time; showing an upgrade prompt *instead of*
      // the result is a paywall in front of work they already did. Non-Elite
      // coaches get the Elite prompt from the reveal's Done button instead — which
      // is what App.tsx's own comment has always claimed happened.
      setPhase('reveal');
      setSavedPlayers(allRanked);
      return;
    }

    const next = unscouted[nextIndex];
    setCurrentIndex(nextIndex);
    setAttrs(mergeAttributes(next?.attributes));
    setIsPitcher((next?.attributes?.pitching?.velocity ?? 0) > 0 || (next?.attributes?.pitching?.control ?? 0) > 0);
    setSaving(false);
  };

  const handleBack = () => {
    if (currentIndex === 0) { onClose(); return; }
    const prev = unscouted[currentIndex - 1];
    setCurrentIndex(currentIndex - 1);
    setAttrs(mergeAttributes(prev?.attributes));
    setIsPitcher((prev?.attributes?.pitching?.velocity ?? 0) > 0 || (prev?.attributes?.pitching?.control ?? 0) > 0);
  };

  // ── Reveal screen ────────────────────────────────────────────────────────────
  if (phase === 'reveal') {
    return (
      <div className="fixed inset-0 bg-surface z-50 flex flex-col overflow-hidden">
        <header
          className="px-6 pb-6 flex items-center justify-between border-b border-hairline"
          style={{ paddingTop: 'calc(env(safe-area-inset-top) + 14px)' }}
        >
          <div>
            <p className="text-[10px] font-semibold text-accent-ink uppercase tracking-widest">Scouting Complete</p>
            <h1 className="text-xl font-black italic uppercase tracking-tight">Your Roster, Ranked</h1>
          </div>
          <button onClick={onComplete} className="text-ink-2 hover:text-ink transition-colors">
            <X className="w-5 h-5" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto p-6 space-y-2">
          {savedPlayers.map((p, i) => (
            <div key={p.id} className="flex items-center gap-4 bg-canvas border border-hairline rounded-2xl p-4">
              <span className="text-ink-2 text-xs font-black w-5 text-right shrink-0">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <p className="font-black text-sm uppercase truncate">{p.name}</p>
                <p className="text-[11px] text-ink-2">#{p.number}{p.primaryPosition ? ` · ${p.primaryPosition}` : ''}</p>
              </div>
              <div className="w-10 h-10 bg-ink rounded-xl flex items-center justify-center shrink-0">
                <span className="text-sm font-black text-surface">{p.ovr ?? '—'}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="p-6 border-t border-hairline">
          <button
            // Non-Elite: the Elite pitch comes AFTER the coach has seen their own
            // ranked roster, never instead of it. The X above just closes — backing
            // out is not the moment to sell.
            onClick={() => (isElite ? onComplete() : onRequestEliteTrial(savedPlayers))}
            className="w-full py-4 bg-ink text-surface font-black rounded-2xl uppercase italic text-lg"
          >
            Done →
          </button>
        </div>
      </div>
    );
  }

  // ── Scouting screen ──────────────────────────────────────────────────────────
  if (!player) return null;

  const progress = currentIndex / unscouted.length;

  const ATTRIBUTE_SECTIONS = [
    { key: 'hitting', label: 'Hitting', attrs: ['contact', 'power', 'eye', 'approach'] },
    { key: 'fielding', label: 'Fielding', attrs: player?.primaryPosition === 'C' ? ['glove', 'arm', 'block'] : ['glove', 'arm'] },
    { key: 'intangibles', label: 'Intangibles', attrs: ['speed', 'iq', 'clutch'] },
  ];

  return (
    <div className="fixed inset-0 bg-surface z-50 flex flex-col overflow-hidden">
      {/* Header — safe-area inset so the back/close buttons clear the status-bar
          clock on notched iPhones (was p-5 only, tucking under the clock). */}
      <header
        className="px-5 pb-5 flex items-center gap-4 border-b border-hairline shrink-0"
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 14px)' }}
      >
        <button onClick={handleBack} className="text-ink-2 hover:text-ink transition-colors">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">
            Scout Your Roster · {currentIndex + 1} of {unscouted.length}
          </p>
          {/* Progress bar */}
          <div className="mt-1.5 h-1 bg-raised rounded-full overflow-hidden">
            <div
              className="h-full bg-ink rounded-full transition-all duration-300"
              style={{ width: `${progress * 100}%` }}
            />
          </div>
        </div>
        <button onClick={onClose} className="text-ink-2 hover:text-ink transition-colors">
          <X className="w-4 h-4" />
        </button>
      </header>

      {/* Player identity + live OVR */}
      <div className="p-6 flex items-center gap-5 border-b border-hairline shrink-0">
        <div className="w-14 h-14 bg-canvas border border-hairline rounded-2xl flex items-center justify-center shrink-0">
          <span className="text-xl font-black text-ink-2">#{player.number}</span>
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-black italic uppercase tracking-tight truncate">{player.name}</h2>
          <p className="text-xs text-ink-2 font-bold">{player.primaryPosition || 'No position set'}</p>
        </div>
        {/* Live OVR badge */}
        <div className="shrink-0 flex flex-col items-center">
          <div className="w-14 h-14 bg-ink rounded-2xl flex items-center justify-center shadow-[0_0_20px_rgba(16,185,129,0.3)]">
            <span className="text-2xl font-black text-surface">{liveOVR}</span>
          </div>
          <span className="text-[9px] font-semibold text-accent-ink uppercase tracking-widest mt-1">OVR</span>
        </div>
      </div>

      {/* Attribute sections */}
      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {ATTRIBUTE_SECTIONS.map(section => (
          <div key={section.key} className="space-y-3">
            <h4 className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">{section.label}</h4>
            <div className="space-y-2">
              {section.attrs.map(attrKey => {
                const val = attrs[section.key]?.[attrKey] ?? 3;
                return (
                  <div key={attrKey} className="flex items-center justify-between bg-canvas border border-hairline rounded-xl px-4 py-3">
                    <span className="text-xs font-bold uppercase text-ink-2 w-20">{attrKey}</span>
                    <div className="flex items-center gap-2">
                      {[1, 2, 3, 4, 5].map(num => (
                        <button
                          key={num}
                          type="button"
                          onClick={() => updateAttr(section.key, attrKey, num)}
                          className={cn(
                            "w-9 h-9 rounded-lg text-xs font-black transition-all",
                            val === num
                              ? "bg-ink text-surface scale-110 shadow-[0_0_10px_rgba(16,185,129,0.4)]"
                              : "bg-raised text-ink-2 hover:bg-hairline"
                          )}
                        >
                          {num}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Pitching section with toggle */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Pitching</h4>
            <button
              type="button"
              onClick={() => setIsPitcher(v => !v)}
              className={cn(
                "px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest transition-all border",
                isPitcher
                  ? "bg-canvas text-accent-ink border-ink-mute"
                  : "bg-raised text-ink-2 border-ink-mute"
              )}
            >
              {isPitcher ? 'Pitcher ✓' : 'Not a Pitcher'}
            </button>
          </div>
          {isPitcher && (
            <div className="space-y-2">
              {['velocity', 'control'].map(attrKey => {
                const val = attrs.pitching?.[attrKey] ?? 0;
                return (
                  <div key={attrKey} className="flex items-center justify-between bg-canvas border border-hairline rounded-xl px-4 py-3">
                    <span className="text-xs font-bold uppercase text-ink-2 w-20">{attrKey}</span>
                    <div className="flex items-center gap-2">
                      {[1, 2, 3, 4, 5].map(num => (
                        <button
                          key={num}
                          type="button"
                          onClick={() => updateAttr('pitching', attrKey, num)}
                          className={cn(
                            "w-9 h-9 rounded-lg text-xs font-black transition-all",
                            val === num
                              ? "bg-ink text-surface scale-110 shadow-[0_0_10px_rgba(16,185,129,0.4)]"
                              : "bg-raised text-ink-2 hover:bg-hairline"
                          )}
                        >
                          {num}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Next button */}
      <div className="p-5 border-t border-hairline shrink-0">
        <button
          onClick={saveAndAdvance}
          disabled={saving}
          className="w-full py-4 bg-ink hover:bg-ink disabled:opacity-50 text-surface font-black rounded-2xl uppercase italic text-lg transition-colors"
        >
          {saving ? 'Saving…' : currentIndex + 1 === unscouted.length ? 'Finish Scouting →' : 'Next Player →'}
        </button>
      </div>
    </div>
  );
};
